import { useEffect, useRef } from "react";

import { GRID_ID_PREFIX, gridIds, parseGridCellId, type GridKeyHandlers, type GridKeys } from "./gridKeyContract";

// web-rnw twin of ./useGridKeys.ts (#336 slice 6 part 2b, brief §7.3): the
// wide grid's keys, clipboard and mouse. They act once a click or the focus
// has gone into the grid, until one goes elsewhere, so the page keeps its
// arrows. ⛔ Not inside a text field: there the keys are the field's (the
// cell's own field keeps Enter, Esc and Tab). Listened to in the capture
// phase, so a cell's own Enter (a press) does not answer as well.
// This package compiles without the DOM lib: the little used is declared here.

type El = {
  id?: string;
  tagName?: string;
  isContentEditable?: boolean;
  closest?: (sel: string) => El | null;
  contains?: (other: unknown) => boolean;
  focus?: () => void;
};
type Ev = {
  target: El | null;
  preventDefault: () => void;
  stopPropagation: () => void;
};
type KeyEv = Ev & { key: string; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean; altKey: boolean };
type PointerEv = Ev & { button: number; buttons: number; pointerType: string; shiftKey: boolean };
type ClipEv = Ev & { clipboardData: { getData: (t: string) => string; setData: (t: string, v: string) => void } | null };
type Listener = (e: never) => void;
declare const window: {
  addEventListener: (type: string, fn: Listener, capture?: boolean) => void;
  removeEventListener: (type: string, fn: Listener, capture?: boolean) => void;
  getSelection?: () => { removeAllRanges: () => void } | null;
};
declare const document: { getElementById: (id: string) => El | null; activeElement: unknown };

const isTextTarget = (el: El | null): boolean =>
  !!el && (el.isContentEditable === true || el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT");

const ARROWS: Record<string, [number, number]> = {
  ArrowUp: [-1, 0],
  ArrowDown: [1, 0],
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1],
};

/** The cell or fill handle of grid `grid` under `el`; null for another grid's. */
const cellOf = (grid: string, el: El | null) => {
  const hit = el?.closest?.(`[id^="${GRID_ID_PREFIX}"]`);
  if (!hit?.id) return null;
  return hit.id === gridIds(grid).fill ? "fill" : parseGridCellId(grid, hit.id);
};

export function useGridKeys(handlers: GridKeyHandlers): GridKeys {
  const h = useRef(handlers);
  h.current = handlers;
  const root = useRef<El | null>(null);
  const inside = useRef(false);

  useEffect(() => {
    let drag: { mode: "select"; from: { row: number; col: number } } | { mode: "fill" } | null = null;
    let swallowClick = false;
    const within = (el: El | null) => !!el && !!root.current?.contains?.(el);
    const stop = (e: Ev) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onKey = (e: KeyEv) => {
      const k = h.current;
      if (!inside.current || !k.active || k.editing || isTextTarget(e.target)) return;
      const arrow = ARROWS[e.key];
      const mod = (e.ctrlKey || e.metaKey) && !e.altKey;
      if (arrow) k.move(arrow[0], arrow[1], e.shiftKey);
      else if (e.key === "Tab" && !mod) {
        if (!k.tab(e.shiftKey)) return;
      } else if (e.key === "Enter" || e.key === "F2") k.edit();
      else if (e.key === "Escape") k.collapse();
      else if (mod && (e.key === "d" || e.key === "D")) {
        if (!k.fill("down")) return;
      } else if (mod && (e.key === "r" || e.key === "R")) {
        if (!k.fill("right")) return;
      }
      else if (!mod && !e.altKey && e.key.length === 1) {
        // Only a character the lens takes opens the field; «?», «/» stay the browser's.
        if (!k.edit(e.key)) return;
      }
      else return;
      stop(e);
    };
    const onClip = (kind: "copy" | "paste") => (e: ClipEv) => {
      const k = h.current;
      if (!inside.current || !k.active || k.editing || isTextTarget(e.target) || !e.clipboardData) return;
      if (kind === "paste") {
        if (!k.paste(e.clipboardData.getData("text/plain"))) return;
      } else {
        const text = k.copy();
        if (text === null) return;
        e.clipboardData.setData("text/plain", text);
      }
      e.preventDefault();
    };
    const onDown = (e: PointerEv) => {
      swallowClick = false;
      drag = null;
      inside.current = within(e.target);
      if (!inside.current || e.button !== 0 || e.pointerType === "touch" || isTextTarget(e.target)) return;
      const cell = cellOf(h.current.grid, e.target);
      if (cell === "fill") drag = { mode: "fill" };
      else if (cell && e.shiftKey) {
        h.current.extendTo(cell);
        swallowClick = true;
      } else if (cell) drag = { mode: "select", from: cell };
    };
    const onMove = (e: PointerEv) => {
      if (!drag || (e.buttons & 1) === 0) return;
      const cell = cellOf(h.current.grid, e.target);
      if (drag.mode === "fill") h.current.fillOver(cell === "fill" ? null : cell);
      else if (cell && cell !== "fill" && (cell.row !== drag.from.row || cell.col !== drag.from.col)) {
        h.current.dragFrom(drag.from, cell);
        swallowClick = true;
      } else return;
      window.getSelection?.()?.removeAllRanges();
    };
    const onUp = () => {
      if (drag?.mode === "fill") {
        h.current.fillEnd();
        swallowClick = true;
      }
      drag = null;
    };
    // A drag or a Shift-click is not also a tap on the cell it ends on.
    const onClick = (e: Ev) => {
      if (!swallowClick) return;
      swallowClick = false;
      stop(e);
    };
    // Right click, Menu, Shift+F10: the range actions, in place of the browser's.
    const onContext = (e: Ev) => {
      if (!within(e.target) || isTextTarget(e.target)) return;
      const cell = cellOf(h.current.grid, e.target);
      if (!cell || cell === "fill" || !h.current.contextMenu(cell)) return;
      stop(e);
    };
    const onFocus = (e: Ev) => {
      inside.current = within(e.target);
    };
    const copy = onClip("copy");
    const paste = onClip("paste");
    const on: [string, Listener, boolean][] = [
      ["keydown", onKey as Listener, true],
      ["copy", copy as Listener, false],
      ["paste", paste as Listener, false],
      ["pointerdown", onDown as Listener, true],
      ["pointermove", onMove as Listener, false],
      ["pointerup", onUp as Listener, false],
      ["click", onClick as Listener, true],
      ["contextmenu", onContext as Listener, true],
      ["focusin", onFocus as Listener, false],
    ];
    for (const [type, fn, capture] of on) window.addEventListener(type, fn, capture);
    return () => {
      for (const [type, fn, capture] of on) window.removeEventListener(type, fn, capture);
    };
  }, []);

  // The focus follows the open cell, so a screen reader says where it went.
  const { anchorId, editing } = handlers;
  useEffect(() => {
    if (!inside.current || editing || !anchorId) return;
    const el = document.getElementById(anchorId);
    if (el && document.activeElement !== el) el.focus?.();
  }, [anchorId, editing]);

  const keys = useRef<GridKeys>({
    rootRef: (node: unknown) => {
      root.current = node as El | null;
    },
    hasKeys: true,
  });
  return keys.current;
}
